import { Box, CircularProgress, Dialog, Fade, Stack, Typography } from '@mui/material';
import type { StatusNotaFiscalTotem } from '../../api';
import { BlocoNotaFiscalTotem, type ContagemEspera } from '../../components/totem/nota-fiscal-totem';
import type { ImpressaoCupomNota } from '../../lib/impressao-totem';
import { CabecalhoSucesso } from '../totem/checkout/payment-screen/pos-pagamento/pos-pagamento-layout';
import ScreenHeader from '../totem/checkout/components/screen-header';

/**
 * Tempo (ms) que a senha fica sozinha na tela antes de a espera da nota (ou a pergunta do
 * cupom) entrar. No totem Windows quem imprime a senha é o servidor, e a tela não fica
 * sabendo quando o papel saiu: a troca é por tempo, o bastante para ler o número.
 */
export const PAUSA_SENHA_TOTEM_MS = 4_000;

/** Tempo mínimo (ms) da tela da nota depois de aberta: nota que resolve logo em seguida não a faz piscar. */
export const PAUSA_NOTA_TOTEM_MS = 1_500;

/** Uma coisa por vez: o registro do pedido, depois a senha, depois a nota. */
export type FaseEsperaTotem = 'REGISTRANDO' | 'SENHA' | 'NOTA';

/**
 * TOTEM: espera bloqueante entre o envio do pedido e o momento em que o cliente
 * tem tudo o que precisa levar do aparelho.
 *
 * Três fases, uma tela para cada. Primeiro o REGISTRO (o Sischef ainda vai gerar o
 * sequencial): antes da senha não há nada de útil para mostrar. Depois a SENHA, sozinha —
 * empilhada com a nota, a tela ficava confusa e o número disputava atenção com a espera.
 *
 * Por fim, nas lojas que emitem NFC-e no totem, a NOTA: a espera continua até o cupom ser
 * enviado para a impressora. Sem isso a senha saía, a tela fechava e o cupom aparecia
 * alguns segundos depois — com o cliente já de costas, achando que a impressão tinha
 * falhado. A senha segue pequena no topo, para não ser esquecida numa espera de até 30 s.
 */
export function EsperaTotem({
  fase,
  statusNota,
  contagem,
  senha,
  senhaImpressa = true,
  referenciaPedido,
  impressaoCupom = 'AUTOMATICA',
  cabecalho,
}: {
  fase: FaseEsperaTotem;
  statusNota: StatusNotaFiscalTotem | null;
  /** Contagem da espera fiscal, exibida no bloco da nota. */
  contagem?: ContagemEspera | null;
  senha: number | null;
  /** A loja imprime a senha? Sem papel, o cliente precisa guardar o número. */
  senhaImpressa?: boolean;
  referenciaPedido?: string | null;
  /** Em "Perguntar ao cliente" o cupom só sai se ele pedir — a espera não promete papel. */
  impressaoCupom?: ImpressaoCupomNota;
  /** O cabeçalho das outras telas do totem; com ele, o número do pedido vai no subtítulo. */
  cabecalho?: { subtitulo: string; logoUrl: string | null };
}): JSX.Element {
  const referencia = referenciaPedido && !cabecalho ? (
    <Typography variant="body2" color="text.secondary">
      Pedido #{referenciaPedido}
    </Typography>
  ) : null;

  return (
    // Sem animação de entrada: o fade do Dialog deixa a tela de trás aparecer quando ele volta depois da pergunta do cupom.
    <Dialog
      open
      fullScreen
      slotProps={{ transition: { appear: false }, paper: { sx: { bgcolor: 'background.default' } } }}
    >
      {/* Fora do Fade: o cabeçalho fica parado enquanto as fases trocam. */}
      {cabecalho && <ScreenHeader title="Seu pedido" subTitle={cabecalho.subtitulo} logoUrl={cabecalho.logoUrl} />}

      <Fade key={fase} in timeout={400}>
        <Stack
          sx={{
            p: 4,
            gap: 4,
            flex: 1,
            minHeight: 0,
            position: 'relative',
            textAlign: 'center',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {fase === 'REGISTRANDO' && (
            <>
              <CircularProgress size={92} thickness={3} />

              <Box>
                <Typography variant="h3" fontWeight={800} sx={{ lineHeight: 1.15 }}>
                  Registrando seu pedido
                </Typography>

                <Typography variant="h6" color="text.secondary" sx={{ mt: 1.5 }}>
                  Aguarde um instante, sua senha aparece aqui.
                </Typography>
              </Box>

              {referencia}
            </>
          )}

          {fase === 'SENHA' && (
            <>
              <CabecalhoSucesso titulo="Pedido confirmado!" />

              <Box>
                <Typography variant="overline" color="text.secondary">
                  Sua senha
                </Typography>

                <Typography
                  variant="h1"
                  fontWeight={800}
                  color="primary.main"
                  sx={{ lineHeight: 1, fontSize: { xs: '4.5rem', md: '6rem' } }}
                >
                  {senha}
                </Typography>

                {/* É a única ação desta tela: maior e mais escura que um texto de apoio. */}
                <Typography variant="h5" color="text.primary" sx={{ mt: 2 }}>
                  {senhaImpressa ? 'Retire sua senha na impressora.' : 'Guarde o número da sua senha.'}
                </Typography>
              </Box>

              {referencia}
            </>
          )}

          {fase === 'NOTA' && (
            <>
              {senha != null && (
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ position: 'absolute', top: 32, left: 0, right: 0, alignItems: 'baseline', justifyContent: 'center' }}
                >
                  <Typography variant="h6" color="text.secondary">
                    Senha
                  </Typography>

                  <Typography variant="h4" fontWeight={800} color="primary.main">
                    {senha}
                  </Typography>
                </Stack>
              )}

              <BlocoNotaFiscalTotem status={statusNota} contagem={contagem} impressao={impressaoCupom} comSenha={senhaImpressa} />
            </>
          )}
        </Stack>
      </Fade>
    </Dialog>
  );
}

export default EsperaTotem;
